import { useEffect, useState } from 'react'

const COUNTDOWN_KEY = 'bttf_countdown_end'
const DURATION_MS = 4 * 60 * 60 * 1000 // 4 hours in ms

function getOrCreateEndTime(): number {
  if (typeof window === 'undefined') return Date.now() + DURATION_MS
  const stored = localStorage.getItem(COUNTDOWN_KEY)
  if (stored) {
    const end = parseInt(stored, 10)
    // If still in the future, use it
    if (end > Date.now()) return end
  }
  // Create new end time
  const newEnd = Date.now() + DURATION_MS
  localStorage.setItem(COUNTDOWN_KEY, newEnd.toString())
  return newEnd
}

export function useCountdown() {
  const [endTime, setEndTime] = useState<number>(() => getOrCreateEndTime())
  const [secondsLeft, setSecondsLeft] = useState<number>(() => {
    const end = getOrCreateEndTime()
    return Math.max(0, Math.floor((end - Date.now()) / 1000))
  })

  useEffect(() => {
    const tick = () => {
      const now = Date.now()
      const remaining = Math.max(0, Math.floor((endTime - now) / 1000))
      setSecondsLeft(remaining)

      if (remaining === 0) {
        // Reset countdown
        const newEnd = Date.now() + DURATION_MS
        localStorage.setItem(COUNTDOWN_KEY, newEnd.toString())
        setEndTime(newEnd)
        // Dispatch event so other components sync
        window.dispatchEvent(
          new CustomEvent('countdownReset', { detail: newEnd }),
        )
      }
    }

    const interval = setInterval(tick, 1000)
    tick() // run immediately

    // Sync across components
    const handleReset = (e: CustomEvent) => {
      setEndTime(e.detail)
    }
    const handleStorage = (e: StorageEvent) => {
      if (e.key === COUNTDOWN_KEY && e.newValue) {
        const newEnd = parseInt(e.newValue, 10)
        setEndTime(newEnd)
      }
    }
    window.addEventListener('countdownReset', handleReset as EventListener)
    window.addEventListener('storage', handleStorage)

    return () => {
      clearInterval(interval)
      window.removeEventListener('countdownReset', handleReset as EventListener)
      window.removeEventListener('storage', handleStorage)
    }
  }, [endTime])

  const hours = Math.floor(secondsLeft / 3600)
  const minutes = Math.floor((secondsLeft % 3600) / 60)
  const seconds = secondsLeft % 60

  const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`

  // Bar: starts at 16.6% (4/24h), depletes to 0% over 4 hours
  const totalSeconds = 4 * 3600
  const barPct = (secondsLeft / totalSeconds) * 16.6

  return { formatted, secondsLeft, barPct }
}
